import { Button } from '@/components'
import { useAxios } from '@/context/axiosContext'
import { DetailMap } from '@/pages/golf/_components/detailMap'
import { DetailInfoGraph } from '@/pages/golf/detailInfo/_components/detailInfoGraph'
import {
  Container,
  PriceCellHeader,
  PriceRow,
  Table,
  TableCell,
  TableCellHeader,
  TableRow,
  Title,
} from '@/pages/golf/detailInfo/_components/style'
import {
  generatePeriod,
  mapMonthDataToGraph,
  PeriodAvgData,
  regionAvgPriceItem,
  useGolfSearchData,
} from '@/pages/main/_components/golfSearch/useGolfSearchData'
import { GraphDataState, setSearchParams } from '@/redux/slices/golfSearchSlice'
import numberUtils from '@/utils/numberUtils'
import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { WeekGraph } from '../detailSearch/_components/graphs/weekGraph'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '@/redux/store'
import { useRedis } from '@/hooks/useRedis'
import { useResponsive } from '@/hooks/useResponsive'
import './detailInfo.css'

interface CommonResponse {
  resultCode: string
  resultMsg: string
}

interface GolfDetailData {
  cmpnyCd: string
  bizCd: string //업체코드
  bizNm: string //업체명
  bizGubn: string //업체구분
  ntnCd: string
  areaCd: string //지역구분
  areaNm: string //지역명
  baseDomain: string //기본도메인
  chrgeDomain: string //요금도메인
  addr: string //주소
  phone: string | null //전화번호
  email: string | null //이메일
  useYn: string //사용여부
  mshipYn: string | null //회원제여부
  batchYn: string //배치여부
  uri: string
  rem: string
  cstRem: string
  schrTag: string | null
  etc1: string | null
  etc2: string | null
  etc3: string | null
  etc4: string | null
  etc5: string | null
  regId: string
  updId: string
  regDate: string
  updDate: string
  page: string | null
  pageStart: string | null
  pageEnd: string | null
  rnum: string | null
  srchStr: string | null
  seq: string | null
  chngDt: string | null
  manualYn: string | null //수기관리여부
  n01: number | null // 비회원(주중) 가격
  n02: number | null // 비회원(주말) 가격
  m01: number | null // 회원(주중) 가격
  m02: number | null // 회원(주말) 가격
  coordinateX: string
  coordinateY: string
  holeType: string
  confidenceLvlCd: string
}

interface ApiResponse {
  common: CommonResponse
  data: {
    golfBizData: GolfDetailData
    golfPriceData: GolfDetailData
    monthAvgPriceData: PeriodAvgData[] // 월평균 가격 데이터
    monthAvgPriceByRegionData: PeriodAvgData[] // 지역별 월평균 가격 데이터
    weekAvgPriceByRegionData: PeriodAvgData[] // 지역별 주평균 가격 데이터
    regionAvgPriceData: regionAvgPriceItem[] //지역별 현재 가격 데이터
  }
}

interface GolfDetailState {
  golfBizData: GolfDetailData | null
  golfPriceData: GolfDetailData | null
  monthAvgPriceData: GraphDataState['monthData']
  monthAvgPriceByRegionData: GraphDataState['monthAvgPriceByRegion']
  //weekAvgPriceByRegionData: GraphDataState['weekAvgPriceByRegion']
  regionAvgPriceData: GraphDataState['regionAvgPrice']
}

export const DetailInfo = () => {
  const location = useLocation()
  const axios = useAxios()
  const dispatch = useDispatch()

  const navigate = useNavigate()
  const [regionWeekData, setRegionWeekData] = useState<PeriodAvgData[]>([])
  //onst { bizCd, bizNm, areaCd, holeType } = location.state || {}
  const { bizCd, bizNm, golfAreaCd, isNationwide } = location.state || {}
  const { isMobile, isTablet, isDesktop } = useResponsive()
  const [data, setData] = useState<GolfDetailState>({
    golfBizData: null,
    golfPriceData: null,
    monthAvgPriceData: [],
    monthAvgPriceByRegionData: [],
    //weekAvgPriceByRegionData: [],
    regionAvgPriceData: [],
  })
  //셀렉터 값이 빈배열로 내려와서, hook호출함
  // useGolfSearchData()
  const { weekData, weekAvgPriceByRegion } = useSelector(
    (state: RootState) => state.golfSearch.graphData,
  )
  // const { areaCd, holeType } = useSelector(
  //   (state: RootState) => state.golfSearch.searchParams,
  // )

  // ✅ searchParams를 먼저 가져오기
  const searchParams = useSelector(
    (state: RootState) => state.golfSearch.searchParams,
  )
  const { areaCd, holeType } = searchParams

  const safeWeekData = weekData && weekData.length ? weekData : [[], [], [], []]
  const isWeekDataEmpty = safeWeekData.every((arr) =>
    arr.every((item) => item === 0),
  )
  const { getComCodeValue } = useRedis()

  useEffect(() => {
    if (bizCd) {
      getDetailData()
    }
  }, [])

  // useEffect(() => {
  //   if (golfAreaCd) {
  //     // 즉시 Redux의 areaCd를 골프장 지역으로 변경
  //     dispatch(
  //       setSearchParams({
  //         ...searchParams,
  //         areaCd: golfAreaCd, // 골프장의 실제 지역코드
  //       }),
  //     )
  //   }
  // }, []) // 마운트 시에만 실행

  useEffect(() => {
    // 컴포넌트 마운트 시 실행할 로직이 있다면 여기에
    if (isNationwide && golfAreaCd) {
      dispatch(
        setSearchParams({
          ...searchParams,
          areaCd: golfAreaCd, // 골프장의 실제 지역코드
        }),
      )
      localStorage.setItem('isNationwide', String(isNationwide))
    }
    // cleanup 함수 - 컴포넌트 언마운트 시 실행
    return () => {
      // 전국에서 온 경우만 초기화
      if (isNationwide) {
        localStorage.setItem('prevPage', 'detailInfo')
        localStorage.setItem('prevIsNationwide', String(isNationwide || false))
        localStorage.setItem('prevAreaCode', '')
        dispatch(
          setSearchParams({
            ...searchParams,
            areaCd: '',
          }),
        )
      }
      // cleanup에서는 localStorage에만 저장
    }
  }, []) // 빈 의존성 배열로 마운트/언마운트 시에만 실행

  // 기존 useEffect를 이것으로 교체하세요

  useEffect(() => {
    window.scrollTo(0, 0) // behavior: 'smooth' 없이 즉시 이동
  }, [])

  const getDetailData = async () => {
    try {
      const { data: responseData } = await axios.get<ApiResponse>(
        '/golf/detail',
        {
          params: {
            bizCd,
            areaCd: areaCd, // Redux의 areaCd 사용 (사용자 선택값)
            holeType: holeType || '18',
          },
        },
      )

      if (responseData.common.resultCode === 'G0200') {
        const {
          golfBizData,
          golfPriceData,
          monthAvgPriceData,
          monthAvgPriceByRegionData,
          weekAvgPriceByRegionData,
          regionAvgPriceData,
        } = responseData.data

        const monthsArr = generatePeriod('month')
        const updatedGraphData = mapMonthDataToGraph(
          monthAvgPriceData,
          monthsArr,
        )

        //console.table(weekAvgPriceByRegionData, ['week', 'n01', 'n02', 'm01', 'm02'])

        // console.log('DetailInfo API 응답:', {
        //   weekAvgPriceByRegionData: weekAvgPriceByRegionData?.length,
        //   golfBizArea: golfBizData?.areaCd,
        //   selectedArea: areaCd,
        // })

        setData({
          golfBizData: golfBizData,
          golfPriceData: golfPriceData,
          monthAvgPriceData: updatedGraphData,
          monthAvgPriceByRegionData: monthAvgPriceByRegionData,
          //weekAvgPriceByRegionData: weekAvgPriceByRegionData,
          regionAvgPriceData: regionAvgPriceData,
        })

        //console.log('weekAvgPriceByRegionData', weekAvgPriceByRegionData)
      }
    } catch (err) {
      console.log(err)
    }
  }

  const handleBack = () => {
    const prevPage = location.state.prevPage
    localStorage.setItem('prevPage', 'detailInfo')
    localStorage.setItem('prevIsNationwide', String(isNationwide || false))

    if (isNationwide) {
      dispatch(
        setSearchParams({
          ...searchParams,
          areaCd: '',
        }),
      )
    }

    navigate(-1)
  }

  return (
    <>
      {!isMobile ? (
        <Container>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <Title>골프장 상세 정보</Title>
            <Button onClick={handleBack} color="neverbe" height={'38px'}>
              {location.state.prevPage === 'detail' ? '골프장검색' : '메인'}
              으로 돌아가기
            </Button>
          </div>

          {data.golfBizData && (
            <Table>
              <tbody>
                <TableRow>
                  <TableCellHeader>골프장</TableCellHeader>
                  <TableCell>{data.golfBizData.bizNm}</TableCell>
                  <TableCellHeader>지역</TableCellHeader>
                  <TableCell>{data.golfBizData.areaNm}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCellHeader>홀 타입</TableCellHeader>
                  <TableCell>
                    {getComCodeValue(
                      'HOLE_TYPE',
                      data.golfBizData.holeType || '',
                    )?.codeNm || '-'}
                  </TableCell>
                  <TableCellHeader>회원제 유형</TableCellHeader>
                  <TableCell>
                    {getComCodeValue('MSHIP_YN', data.golfBizData.mshipYn || '')
                      ?.codeNm || '-'}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCellHeader>
                    {
                      <span
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '2px', // 텍스트와 아이콘 사이 간격
                        }}
                        title="골프장 금액에 대한 정확도 정보입니다."
                      >
                        정확도
                        <img
                          src="/svg/question-mark-circle-outline-icon.svg"
                          alt="도움말"
                          width="12"
                          height="12"
                          style={{ verticalAlign: 'middle' }}
                        />
                      </span>
                    }
                  </TableCellHeader>
                  <TableCell>
                    {getComCodeValue(
                      'CONFIDENCE_LVL_CD',
                      data.golfBizData.confidenceLvlCd || '',
                    )?.codeNm || '-'}
                  </TableCell>
                  <TableCellHeader>전화번호</TableCellHeader>
                  <TableCell>{data.golfBizData.phone || '-'}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCellHeader>이메일</TableCellHeader>
                  <TableCell>{data.golfBizData.email || '-'}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCellHeader>주소</TableCellHeader>
                  <TableCell colSpan={3}>{data.golfBizData.addr}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCellHeader>홈페이지</TableCellHeader>
                  <TableCell colSpan={3}>
                    <a
                      href={data.golfBizData.baseDomain}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {data.golfBizData.baseDomain}
                    </a>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCellHeader>비고</TableCellHeader>
                  <TableCell colSpan={3} style={{ whiteSpace: 'pre-line' }}>
                    {data.golfBizData.cstRem}
                  </TableCell>
                </TableRow>
              </tbody>
            </Table>
          )}
          {data.golfPriceData && (
            <Table style={{ marginTop: '50px' }}>
              <tbody>
                <PriceRow>
                  <PriceCellHeader colSpan={4}>요금정보</PriceCellHeader>
                </PriceRow>
                {data.golfPriceData?.n01 !== null &&
                  data.golfPriceData?.n02 !== null && (
                    <TableRow>
                      <TableCellHeader>비회원(주중)</TableCellHeader>
                      <TableCell>
                        {data.golfPriceData.n01 > 0
                          ? numberUtils.formatCurrency(data.golfPriceData.n01)
                          : '갱신중'}
                      </TableCell>
                      <TableCellHeader>비회원(주말)</TableCellHeader>
                      <TableCell>
                        {data.golfPriceData.n02 > 0
                          ? numberUtils.formatCurrency(data.golfPriceData.n02)
                          : '갱신중'}
                      </TableCell>
                    </TableRow>
                  )}
                {data.golfPriceData?.m01 !== null &&
                  data.golfPriceData?.m02 !== null && (
                    <TableRow>
                      <TableCellHeader>회원(주중)</TableCellHeader>
                      <TableCell>
                        {data.golfPriceData.m01 > 0
                          ? numberUtils.formatCurrency(data.golfPriceData.m01)
                          : '갱신중'}
                      </TableCell>
                      <TableCellHeader>회원(주말)</TableCellHeader>
                      <TableCell>
                        {data.golfPriceData.m02 > 0
                          ? numberUtils.formatCurrency(data.golfPriceData.m02)
                          : '갱신중'}
                      </TableCell>
                    </TableRow>
                  )}
              </tbody>
            </Table>
          )}
          <div style={{ marginTop: '20px' }}> * 출처 : 공식 홈페이지</div>
        </Container>
      ) : (
        <div className="container">
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginTop: '15px',
              marginLeft: '2px',
            }}
          >
            <Title>골프장 상세 정보</Title>
            <Button onClick={handleBack} color="neverbe" height={'38px'}>
              {/* {location.state.prevPage === 'detail'
                ? '← 골프장검색'
                : '← 메인으로'} */}
              돌아가기
            </Button>
          </div>
          {data.golfBizData && (
            <>
              <div className="course-card">
                <div className="course-header">
                  <h2>{data.golfBizData.bizNm}</h2>
                </div>

                <div className="info-list">
                  <div className="info-item">
                    <span className="icon">📍</span>
                    <div>
                      <div className="label">지역</div>
                      <div className="value">{data.golfBizData.areaNm}</div>
                    </div>
                  </div>

                  <div className="info-item">
                    <span className="icon">⛳</span>
                    <div>
                      <div className="label">홀 타입</div>
                      <div className="value">
                        {getComCodeValue(
                          'HOLE_TYPE',
                          data.golfBizData.holeType || '',
                        )?.codeNm || '-'}
                      </div>
                    </div>
                  </div>

                  <div className="info-item">
                    <span className="icon">👥</span>
                    <div>
                      <div className="label">회원제 유형</div>
                      <div className="value">
                        {getComCodeValue(
                          'MSHIP_YN',
                          data.golfBizData.mshipYn || '',
                        )?.codeNm || '-'}
                      </div>
                    </div>
                  </div>

                  <div className="info-item">
                    <span className="icon">📊</span>
                    <div>
                      <div className="label">정확도</div>
                      <div className="value">
                        {getComCodeValue(
                          'CONFIDENCE_LVL_CD',
                          data.golfBizData.confidenceLvlCd || '',
                        )?.codeNm || '-'}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {data.golfBizData.cstRem && (
                <div className="notice-card">
                  <div className="notice-icon">⚠️</div>
                  <div className="notice-text">{data.golfBizData.cstRem}</div>
                </div>
              )}
              <div className="contact-card">
                <h3>연락처</h3>
                {data.golfBizData.phone && (
                  <div className="contact-item">
                    <span className="icon">📞</span>
                    <a
                      href={`tel:${data.golfBizData.phone}`}
                      className="contact-link"
                    >
                      {data.golfBizData.phone || '-'}
                    </a>
                  </div>
                )}
                {data.golfBizData.baseDomain && (
                  <div className="contact-item">
                    <span className="icon">🌐</span>
                    <a
                      href={data.golfBizData.baseDomain}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="contact-link"
                    >
                      홈페이지 방문
                    </a>
                  </div>
                )}
                {data.golfBizData.email && (
                  <div className="contact-item">
                    <span className="icon">🌐</span>
                    <a
                      target="_blank"
                      rel="noopener noreferrer"
                      className="contact-link"
                    >
                      이메일
                    </a>
                    {data.golfBizData.email || '-'}
                    <a
                      href={`mailto:${data.golfBizData.email}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="contact-link"
                    >
                      {data.golfBizData.email}
                    </a>
                  </div>
                )}
              </div>

              <div className="address-card">
                <h3>주소</h3>
                <p>{data.golfBizData.addr}</p>
              </div>
            </>
          )}
          <div className="pricing-card">
            <h3>요금 정보</h3>
            {data.golfPriceData && (
              <div className="price-list">
                {data.golfPriceData?.n01 !== null &&
                  data.golfPriceData?.n02 !== null && (
                    <div className="price-item">
                      <div className="price-type">비회원(주중)</div>
                      <div className="price">
                        {data.golfPriceData.n01 > 0
                          ? numberUtils.formatCurrency(data.golfPriceData.n01)
                          : '갱신중'}
                      </div>
                      <div style={{ marginBottom: '10px' }} />
                      <div className="price-type">비회원(주말)</div>
                      <div className="price">
                        {data.golfPriceData.n02 > 0
                          ? numberUtils.formatCurrency(data.golfPriceData.n02)
                          : '갱신중'}
                      </div>
                    </div>
                  )}

                {data.golfPriceData?.m01 !== null &&
                  data.golfPriceData?.m02 !== null && (
                    <div className="price-item member">
                      <div className="price-type">회원(주중)</div>
                      <div className="price">
                        {data.golfPriceData.m01 > 0
                          ? numberUtils.formatCurrency(data.golfPriceData.m01)
                          : '갱신중'}
                      </div>
                      <div style={{ marginBottom: '10px' }} />
                      <div className="price-type">회원(주말)</div>
                      <div className="price">
                        {data.golfPriceData.m02 > 0
                          ? numberUtils.formatCurrency(data.golfPriceData.m02)
                          : '갱신중'}
                      </div>
                    </div>
                  )}
              </div>
            )}
            <div className="price-note">* 출처 : 공식 홈페이지</div>
          </div>
        </div>
      )}
      <div
        style={{
          padding: isMobile ? '10px 15px' : '0',
          wordBreak: 'keep-all',
          lineHeight: '1.4',
          fontSize: isMobile ? '13px' : '14px',
          whiteSpace: 'normal',
          overflow: 'visible',
        }}
      >
        {isMobile && `* 그래프를 옆으로 밀면 더 많은 내용을 볼 수 있어요.`}
      </div>
      <div style={{ padding: isMobile ? '0 15px' : '0' }}>
        {!isWeekDataEmpty && (
          <WeekGraph
            weekData={weekData} // Redux에서 온 전국 데이터
            weekAvgPriceByRegion={weekAvgPriceByRegion}
            //areaCd={data.golfBizData?.areaCd}
            areaCd={areaCd}
            bizCd={bizCd} //detail에서만 별도로 추가
            bizNm={data.golfBizData?.bizNm}
            detailAreaNm={data.golfBizData?.areaNm}
            holeType={holeType || data.golfBizData?.holeType} // Redux 우선, 없으면 골프장 데이터
            isWeekDualGraph={true}
            mshipYn={data?.golfBizData?.mshipYn || ''}
          />
        )}
        <DetailInfoGraph
          regionAvgPriceData={data.regionAvgPriceData}
          monthAvgPriceByRegionData={data.monthAvgPriceByRegionData}
          monthAvgPriceData={data.monthAvgPriceData}
          areaNm={data.golfBizData?.areaNm}
          bizNm={data.golfBizData?.bizNm}
        />
        {data.golfBizData && (
          <DetailMap
            data={[
              {
                addr: data.golfBizData.addr,
                bizNm: data.golfBizData.bizNm,
                // 인터페이스 변경으로 채울수 있는 값 채워주기
                areaCd: data.golfBizData.areaCd,
                areaNm: data.golfBizData.areaNm,
                bizCd: data.golfBizData.bizCd,
                m01: data.golfBizData.m01 || -1,
                m02: data.golfBizData.m02 || -1,
                n01: data.golfBizData.n01 || -1,
                n02: data.golfBizData.n02 || -1,
                mshipYn: data.golfBizData.mshipYn || 'N',
                areaCount: -1,
                coordinateX: data.golfBizData.coordinateX,
                coordinateY: data.golfBizData.coordinateY,
                holeType: data.golfBizData.holeType,
              },
            ]}
          />
        )}
      </div>
    </>
  )
}
